import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    shouldCollectPostalCode,
    hasFailedInstantMatch,
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    TeacherPersonalInfoViewModel,
    Organization,
    TeacherPersonalInfoResponse,
    Locale,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableLocaleChoices,
    LogoComponent,
    MarketConsentWrapper,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    FormFooterComponent as FormFooter,
    PostalCodeComponent,
    ChangeLocaleComponent,
    TeacherSchoolComponent,
    CountryComponentWrapper,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork
} from '@sheerid/jslib';

import { TeacherStatusComponent } from '../FormFields/TeacherStatus/TeacherStatusComponent';
import { TeacherStatusEnum } from '../../lib/types/types';


interface StepTeacherPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepTeacherPersonalInfo = ({ intl, verificationService }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as TeacherPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };

    const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: any) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    const populateTeacherStatus = (): string[] => {
        const teacherStatuses: string[] = [];

        for (const value in TeacherStatusEnum) {
            teacherStatuses.push(value)
        }

        return teacherStatuses;
    };

    const getTeacherStatuses = (): FormSelectChoice[] => {
        const teacherStatuses: string[] = populateTeacherStatus();

        return teacherStatuses
            .map(teacherStatusValues  => ({
                value: teacherStatusValues,
                label: intl.formatMessage({
                    id: `step.personalInfo.teacherStatus.${teacherStatusValues}`,
                    defaultMessage: teacherStatusValues as string,
                }),
            }));
    };

    const teacherStatusValue = {
        value: verificationService.viewModel.metadata ? verificationService.viewModel.metadata['teacherStatus'] : '' as string,
        label: intl.formatHTMLMessage({
            id: `step.personalInfo.selectChoice.${verificationService.viewModel.metadata ? verificationService.viewModel.metadata['teacherStatus'] : '' as string}`,
            defaultMessage:
                `${verificationService.viewModel.metadata ? verificationService.viewModel.metadata['teacherStatus'] : '' as string}`,
        }),
    }

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">


            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and email address must match what is on record with your school." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching Staff-Only Offer" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a teacher, faculty or staff at a K-12 or degree-granting university." />
                            &nbsp;
                            <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            {
                localeChoices.length > 1 ?
                    <ChangeLocaleComponent
                        options={localeChoices}
                        value={viewModel.localeChoice || defaultLocaleChoice}
                        isErrored={false}
                        onChange={(localeChoice: FormSelectChoice<Locale, string>) =>
                            updateLocale(localeChoice)}
                    />
                : null
            }

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
            />

            <TeacherStatusComponent
                onChange={
                    (newValue: FormSelectChoice<string, string>) => {
                        updateTeacherViewModel('metadata', { teacherStatus: (newValue ? newValue.value : '') });
                    }
                }
                value={teacherStatusValue}
                options={getTeacherStatuses()}
            />

            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateTeacherViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateTeacherViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Must be your school-issued email address"
                    />
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: string) => {
                                updateTeacherViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                : null
            }
                {
                    shouldCollectPostalCode(verificationService.programTheme, viewModel) ?
                    <PostalCodeComponent
                        isErrored={!!fieldValidationErrors.postalCode}
                        onChange={(newValue: string) => {
                            updateTeacherViewModel('postalCode', newValue);
                            updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                        }}
                        value={viewModel.postalCode}
                    />
                    :
                    null
                }

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectTeacherPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button as HTMLElement)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyTeacherStatus" defaultMessage="Verify My Teacher Status" />
                            }
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
